// ============================================================
// HornDeliver Navigation System — Centralized Config
// ------------------------------------------------------------
// Single source of truth for the 10 platform modules and their
// role-based visibility, ordering, and routing. Consumed by:
//   - AppHeader   (desktop top-nav)
//   - BottomNav   (mobile bottom-nav + "More" sheet)
//   - ProfileMenu (grouped quick-links)
//   - App.tsx     (route guards / redirects)
//
// Design principles:
//   1. Mobile-first PWA — bottom nav is the primary surface.
//   2. Role-aware — each of the 4 roles (customer, driver,
//      merchant, admin) sees a tailored 4-slot primary set
//      plus the full accessible set inside the "More" sheet.
//   3. Consistency — the same module id resolves to the same
//      icon, label key, and route prefix across every surface.
//   4. Offline-friendly — nav is pure data, no network calls.
// ============================================================

import type { Role } from "@/lib/types";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import {
  LayoutDashboard,
  Package,
  Navigation,
  Wallet,
  MessageCircle,
  Star,
  Megaphone,
  User as UserIcon,
  Settings,
  HelpCircle,
  // admin-specific
  Users,
  BarChart3,
  ShieldCheck,
  ScrollText,
  Power,
  Percent,
  Flag,
  Store as StoreIcon,
  // customer/driver/merchant extras
  Home,
  ShoppingBag,
  Truck,
  Search,
  Bell,
  // sidebar stats icons
  DollarSign,
  TrendingUp,
  // Phase 6: admin tenants + email templates
  Building2,
  Mail,
  type LucideIcon,
} from "lucide-react";

// ------------------------------------------------------------
// Module catalog — the canonical 10 modules + admin sub-modules.
// ------------------------------------------------------------
export type ModuleId =
  | "dashboard"
  | "order"
  | "tracking"
  | "wallets"
  | "message"
  | "subscription"
  | "advertising"
  | "profile"
  | "settings"
  | "help"
  | "alerts"
  | "find"
  | "earnings"
  // admin-only operational modules (surfaced under "More" / sidebar)
  | "admin-users"
  | "admin-analytics"
  | "admin-verifications"
  | "admin-disputes"
  | "admin-services"
  | "admin-stores"
  | "admin-commission"
  | "admin-fraud"
  | "admin-tenants"
  | "admin-email-templates";

export interface NavModule {
  id: ModuleId;
  /** i18n key under `nav.*` — fallback to label if missing. */
  labelKey: string;
  /** Default English label (used when i18n is not yet loaded). */
  label: string;
  icon: LucideIcon;
  /** Hash-route prefix — navigating here lands on this path. */
  route: string;
  /** Group for the More sheet / profile menu. */
  group: "primary" | "account" | "admin" | "support";
  /** Roles that can see this module. Admin sees everything. */
  roles: Role[];
  /** Optional badge-style hint (e.g. "New", "Beta"). */
  badge?: string;
}

const ALL_ROLES: Role[] = ["customer", "provider", "driver", "merchant", "admin"];

// ------------------------------------------------------------
// Module definitions. Order within each group is the display
// order in the More sheet and the desktop top-nav.
// ------------------------------------------------------------
export const MODULES: Record<ModuleId, NavModule> = {
  dashboard: {
    id: "dashboard",
    labelKey: "nav.dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    route: "app", // customer/driver/merchant landing
    group: "primary",
    roles: ALL_ROLES,
  },
  order: {
    id: "order",
    labelKey: "nav.order",
    label: "Orders",
    icon: Package,
    route: "app/jobs",
    group: "primary",
    roles: ALL_ROLES,
  },
  tracking: {
    id: "tracking",
    labelKey: "nav.tracking",
    label: "Tracking",
    icon: Navigation,
    route: "app/track",
    group: "primary",
    roles: ALL_ROLES,
  },
  wallets: {
    id: "wallets",
    labelKey: "nav.wallets",
    label: "Wallets",
    icon: Wallet,
    route: "app/wallet",
    group: "primary",
    roles: ALL_ROLES,
  },
  message: {
    id: "message",
    labelKey: "nav.message",
    label: "Messages",
    icon: MessageCircle,
    route: "app/chat",
    group: "primary",
    roles: ALL_ROLES,
  },
  subscription: {
    id: "subscription",
    labelKey: "nav.subscription",
    label: "Subscription",
    icon: Star,
    route: "app/subscriptions",
    group: "primary",
    roles: ALL_ROLES,
  },
  advertising: {
    id: "advertising",
    labelKey: "nav.advertising",
    label: "Advertising",
    icon: Megaphone,
    route: "admin/ads",
    group: "primary",
    roles: ["merchant", "provider", "admin"],
    badge: "Pro",
  },
  profile: {
    id: "profile",
    labelKey: "nav.profile",
    label: "Profile",
    icon: UserIcon,
    route: "app/profile",
    group: "account",
    roles: ALL_ROLES,
  },
  settings: {
    id: "settings",
    labelKey: "nav.settings",
    label: "Settings",
    icon: Settings,
    route: "app/settings",
    group: "account",
    roles: ALL_ROLES,
  },
  help: {
    id: "help",
    labelKey: "nav.help",
    label: "Help & Supports",
    icon: HelpCircle,
    route: "app/help",
    group: "support",
    roles: ALL_ROLES,
  },
  alerts: {
    id: "alerts",
    labelKey: "nav.alerts",
    label: "Alerts",
    icon: Bell,
    route: "app/notifications",
    group: "primary",
    roles: ALL_ROLES,
  },
  find: {
    id: "find",
    labelKey: "nav.find",
    label: "Find",
    icon: Search,
    route: "app/jobs",
    group: "primary",
    roles: ["driver", "customer", "provider", "merchant"],
  },
  earnings: {
    id: "earnings",
    labelKey: "nav.earnings",
    label: "Earnings",
    icon: DollarSign,
    route: "app/earnings",
    group: "primary",
    roles: ["driver", "admin"],
  },

  // ---- Admin operational modules (under "More" → Admin group) ----
  "admin-users": {
    id: "admin-users",
    labelKey: "nav.users",
    label: "Users",
    icon: Users,
    route: "admin/users",
    group: "admin",
    roles: ["admin"],
  },
  "admin-analytics": {
    id: "admin-analytics",
    labelKey: "nav.analytics",
    label: "Analytics",
    icon: BarChart3,
    route: "admin/analytics",
    group: "admin",
    roles: ["admin"],
  },
  "admin-verifications": {
    id: "admin-verifications",
    labelKey: "nav.verify",
    label: "Verifications",
    icon: ShieldCheck,
    route: "admin/verifications",
    group: "admin",
    roles: ["admin"],
  },
  "admin-disputes": {
    id: "admin-disputes",
    labelKey: "nav.disputes",
    label: "Disputes",
    icon: ScrollText,
    route: "admin/disputes",
    group: "admin",
    roles: ["admin"],
  },
  "admin-services": {
    id: "admin-services",
    labelKey: "nav.services",
    label: "Service Status",
    icon: Power,
    route: "admin/services",
    group: "admin",
    roles: ["admin"],
  },
  "admin-stores": {
    id: "admin-stores",
    labelKey: "nav.stores",
    label: "Stores",
    icon: StoreIcon,
    route: "admin/stores",
    group: "admin",
    roles: ["admin"],
  },
  "admin-commission": {
    id: "admin-commission",
    labelKey: "nav.commissionRules",
    label: "Commission Rules",
    icon: Percent,
    route: "admin/commission-rules",
    group: "admin",
    roles: ["admin"],
  },
  "admin-fraud": {
    id: "admin-fraud",
    labelKey: "nav.fraudFlags",
    label: "Fraud Flags",
    icon: Flag,
    route: "admin/fraud-flags",
    group: "admin",
    roles: ["admin"],
  },
  "admin-tenants": {
    id: "admin-tenants",
    labelKey: "nav.tenants",
    label: "Tenants",
    icon: Building2,
    route: "admin/tenants",
    group: "admin",
    roles: ["admin"],
  },
  "admin-email-templates": {
    id: "admin-email-templates",
    labelKey: "nav.emailTemplates",
    label: "Email Templates",
    icon: Mail,
    route: "admin/email-templates",
    group: "admin",
    roles: ["admin"],
  },
};

// ------------------------------------------------------------
// Primary bottom-nav slots per role.
// 5 slots — matches the DI-Navigation design: Home, Orders, Track, Alerts, Profile.
// No "More" button; secondary modules are in the sidebar (desktop) or
// accessible from the Profile screen (mobile).
// ------------------------------------------------------------
export const PRIMARY_SLOTS: Record<Role, ModuleId[]> = {
  // Customer (Sender): Home, Orders, Track, Alerts, Profile
  customer: ["dashboard", "order", "tracking", "alerts", "profile"],
  // Driver: Home, Find, Orders, Alerts, Profile
  driver: ["dashboard", "find", "order", "alerts", "profile"],
  // Provider (merchant-equivalent): Home, Orders, Track, Alerts, Profile
  provider: ["dashboard", "order", "tracking", "alerts", "profile"],
  // Merchant: Home, Orders, Track, Alerts, Profile
  merchant: ["dashboard", "order", "tracking", "alerts", "profile"],
  // Admin: Dashboard, Track, Verify, Disputes, Profile
  admin: ["dashboard", "tracking", "admin-verifications", "admin-disputes", "profile"],
};

// ------------------------------------------------------------
// Sidebar module lists per role — the full vertical nav for desktop.
// Includes primary + secondary modules in display order, EXCLUDING
// profile/settings/help (those go in the sidebar footer).
// ------------------------------------------------------------
export const SIDEBAR_MODULES: Record<Role, ModuleId[]> = {
  // Admin: Dashboard, Tracking, Subscriptions, Sponsor, Verify, Services, Analytics, Disputes
  admin: [
    "dashboard",
    "tracking",
    "subscription",
    "advertising",
    "admin-verifications",
    "admin-services",
    "admin-analytics",
    "admin-disputes",
  ],
  // Driver: Home, Find, My Orders, Alerts, Earnings, Wallets
  driver: [
    "dashboard",
    "find",
    "order",
    "alerts",
    "earnings",
    "wallets",
  ],
  // Provider: Home, My Orders, Tracking, Subscriptions, Alerts, Advertising
  provider: [
    "dashboard",
    "order",
    "tracking",
    "subscription",
    "alerts",
    "advertising",
  ],
  // Merchant: Home, My Orders, Tracking, Subscriptions, Alerts, Advertising
  merchant: [
    "dashboard",
    "order",
    "tracking",
    "subscription",
    "alerts",
    "advertising",
  ],
  // Customer (Sender): Home, My Orders, Tracking, Alerts, Subscription, Wallets
  customer: [
    "dashboard",
    "order",
    "tracking",
    "alerts",
    "subscription",
    "wallets",
  ],
};

// ------------------------------------------------------------
// Role stats widgets — shown in the sidebar above the role switcher.
// Each stat is a label + a value source (fetched client-side).
// ------------------------------------------------------------
export interface RoleStat {
  id: string;
  labelKey: string;
  label: string;
  icon: LucideIcon;
  /** Query hook key — the sidebar fetches and displays the value. */
  source: "earnings" | "rating" | "revenue" | "orders" | "wallet" | "none";
}

export const ROLE_STATS: Record<Role, RoleStat[]> = {
  admin: [],
  driver: [
    { id: "earnings", labelKey: "nav.earnings", label: "Earnings", icon: DollarSign, source: "earnings" },
    { id: "rating", labelKey: "nav.rating", label: "Rating", icon: Star, source: "rating" },
  ],
  provider: [
    { id: "revenue", labelKey: "nav.revenue", label: "Today's Revenue", icon: TrendingUp, source: "revenue" },
    { id: "orders", labelKey: "nav.ordersCount", label: "Orders", icon: Package, source: "orders" },
  ],
  merchant: [
    { id: "revenue", labelKey: "nav.revenue", label: "Today's Revenue", icon: TrendingUp, source: "revenue" },
    { id: "orders", labelKey: "nav.ordersCount", label: "Orders", icon: Package, source: "orders" },
  ],
  customer: [
    { id: "wallet", labelKey: "nav.walletBalance", label: "Wallet Balance", icon: Wallet, source: "wallet" },
    { id: "pass", labelKey: "nav.hornDeliverPass", label: "HornDeliver Pass", icon: Star, source: "none" },
  ],
};

// ------------------------------------------------------------
// Role display metadata — header label + icon + accent color for sidebar.
// ------------------------------------------------------------
export interface RoleMeta {
  labelKey: string;
  label: string;
  icon: LucideIcon;
  emoji: string;
}

export const ROLE_META: Record<Role, RoleMeta> = {
  admin: { labelKey: "nav.adminMode", label: "Admin Mode", icon: ShieldCheck, emoji: "🛠️" },
  driver: { labelKey: "nav.driverMode", label: "Driver Mode", icon: Truck, emoji: "🛵" },
  provider: { labelKey: "nav.providerMode", label: "Provider Mode", icon: StoreIcon, emoji: "🏪" },
  merchant: { labelKey: "nav.providerMode", label: "Provider Mode", icon: StoreIcon, emoji: "🏪" },
  customer: { labelKey: "nav.senderMode", label: "Sender Mode", icon: Package, emoji: "📦" },
};

// ------------------------------------------------------------
// Effective role: admin users always operate in admin context
// for navigation purposes, regardless of activeRole.
// ------------------------------------------------------------
export function effectiveRole(user: { isAdmin: boolean; activeRole: Role }): Role {
  return user.isAdmin ? "admin" : user.activeRole;
}

// ------------------------------------------------------------
// Role-aware dashboard route.
// The dashboard module's static `route` is "app" (the customer
// landing), but the actual landing route depends on the user's
// effective role:
//   • admin    → "admin"    (#/admin renders AdminDashboard)
//   • merchant → "merchant" (#/merchant renders MerchantDashboardView)
//   • other    → "app"      (#/app renders AppDashboard)
//
// This is the single source of truth for "where does Dashboard go?"
// Used by the sidebar, bottom nav, and header logo so every
// "Dashboard" / "Home" click lands on the correct role-scoped page.
// ------------------------------------------------------------
export function getDashboardRoute(user: {
  isAdmin: boolean;
  activeRole: Role;
}): string {
  if (user.isAdmin) return "admin";
  if (user.activeRole === "merchant") return "merchant";
  return "app";
}

// ------------------------------------------------------------
// Resolve a module's route for a specific user.
// Most modules have a static route, but the `dashboard` module
// is role-aware (see getDashboardRoute). This helper centralizes
// the lookup so every nav surface (sidebar, bottom nav, header
// logo, profile menu) stays in sync.
// ------------------------------------------------------------
export function getModuleRoute(
  user: { isAdmin: boolean; activeRole: Role },
  m: NavModule
): string {
  if (m.id === "dashboard") return getDashboardRoute(user);
  return m.route;
}

// ------------------------------------------------------------
// Get the primary nav modules for a user's effective role.
// Returns 5 modules for the mobile bottom nav.
// ------------------------------------------------------------
export function getPrimaryModules(user: {
  isAdmin: boolean;
  activeRole: Role;
}): NavModule[] {
  const role = effectiveRole(user);
  return PRIMARY_SLOTS[role].map((id) => MODULES[id]);
}

// ------------------------------------------------------------
// Get the sidebar modules for a user's effective role.
// Returns the full vertical nav list for the desktop sidebar.
// ------------------------------------------------------------
export function getSidebarModules(user: {
  isAdmin: boolean;
  activeRole: Role;
}): NavModule[] {
  const role = effectiveRole(user);
  return SIDEBAR_MODULES[role]
    .filter((id) => FEATURE_FLAGS.ADS_ENABLED || id !== "advertising")
    .map((id) => MODULES[id]);
}

// ------------------------------------------------------------
// Get the stats widgets for a user's effective role.
// ------------------------------------------------------------
export function getRoleStats(user: {
  isAdmin: boolean;
  activeRole: Role;
}): RoleStat[] {
  const role = effectiveRole(user);
  return ROLE_STATS[role];
}

// ------------------------------------------------------------
// Get all modules accessible to a user, grouped for the More
// sheet / profile menu. Excludes the primary 4 (already shown
// in the bottom nav) from the More sheet's "primary" group on
// mobile, but includes everything on desktop.
// ------------------------------------------------------------
export function getAccessibleModules(
  user: { isAdmin: boolean; activeRole: Role },
  options: { excludePrimary?: boolean } = {}
): NavModule[] {
  const role = effectiveRole(user);
  const primaryIds = new Set(PRIMARY_SLOTS[role]);

  return Object.values(MODULES).filter((m) => {
    if (!m.roles.includes(role)) return false;
    if (!FEATURE_FLAGS.ADS_ENABLED && m.id === "advertising") return false;
    if (options.excludePrimary && primaryIds.has(m.id)) return false;
    return true;
  });
}

// ------------------------------------------------------------
// Group modules by their `group` for display in the More sheet
// and profile dropdown menu.
// ------------------------------------------------------------
export function groupModules(
  modules: NavModule[]
): Record<NavModule["group"], NavModule[]> {
  const groups: Record<NavModule["group"], NavModule[]> = {
    primary: [],
    account: [],
    admin: [],
    support: [],
  };
  for (const m of modules) groups[m.group].push(m);
  return groups;
}

// ------------------------------------------------------------
// Resolve the active module from a route path.
// Returns the module whose route is the longest prefix match.
// ------------------------------------------------------------
export function getActiveModule(path: string): NavModule | null {
  const clean = path.replace(/^\/+/, "");
  let best: NavModule | null = null;
  let bestLen = 0;
  for (const m of Object.values(MODULES)) {
    if (clean === m.route || clean.startsWith(m.route + "/")) {
      if (m.route.length > bestLen) {
        best = m;
        bestLen = m.route.length;
      }
    }
  }
  // Special-case: bare "app" routes to dashboard.
  if (!best && (clean === "app" || clean === "merchant" || clean === "admin")) {
    return MODULES.dashboard;
  }
  return best;
}

// ------------------------------------------------------------
// Check whether a module is accessible to a user.
// ------------------------------------------------------------
export function canAccessModule(
  user: { isAdmin: boolean; activeRole: Role },
  id: ModuleId
): boolean {
  const role = effectiveRole(user);
  return MODULES[id].roles.includes(role);
}

// ------------------------------------------------------------
// Translate a module's label via the i18n instance, with a
// graceful fallback to the static label.
// ------------------------------------------------------------
export function moduleLabel(m: NavModule, t?: (key: string) => string): string {
  if (!t) return m.label;
  const translated = t(m.labelKey);
  return translated === m.labelKey ? m.label : translated;
}
